// Brillenhaus – Kontaktlinsen-Konfigurator: Werte je Auge wählen, Boxen je Auge, Reichweite und Summe,
// dann beide Augen als eigene Warenkorb-Positionen ("Auge: Rechts/Links") hinzufügen.
import { CartAddEvent } from '@theme/events';

const formatMoney = (cents, format) => {
  const value = (cents / 100).toFixed(2);
  const [int, dec] = value.split('.');
  const withSep = int.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const amount = `${withSep},${dec}`;
  return (format || '€{{amount_with_comma_separator}}').replace(/\{\{\s*amount[a-z_]*\s*\}\}/, amount);
};

function supplyText(pack, days, boxes, eyes = 1) {
  if (!pack || !days || !boxes) return '';
  const totalDays = (pack * days * boxes) / eyes;
  if (totalDays >= 60) return `ca. ${Math.round(totalDays / 30)} Monate`;
  if (totalDays >= 28) return `ca. ${Math.round(totalDays / 30) || 1} Monat${Math.round(totalDays / 30) > 1 ? 'e' : ''}`;
  if (totalDays >= 14) return `ca. ${Math.round(totalDays / 7)} Wochen`;
  return `${totalDays} Tage`;
}

function init(root) {
  if (root.dataset.bhReady) return;
  root.dataset.bhReady = 'true';
  const data = JSON.parse(root.querySelector('[data-bh-lens-data]').textContent);
  const pack = Number(root.dataset.pack) || 0;
  const days = Number(root.dataset.days) || 0;
  const free = Number(root.dataset.free) || 0;
  const eyes = [...root.querySelectorAll('[data-bh-lens-eye]')];
  const same = root.querySelector('[data-bh-lens-same]');
  const summary = root.querySelector('[data-bh-lens-summary]');
  const addBtn = root.querySelector('[data-bh-lens-add]');
  const errorEl = root.querySelector('[data-bh-lens-error]');
  const doneEl = root.querySelector('[data-bh-lens-done]');

  const read = (eye) => {
    const values = [...eye.querySelectorAll('[data-bh-lens-opt]')].map((s) => s.value);
    const qty = Math.max(0, Math.min(10, Number(eye.querySelector('[data-bh-lens-qty]').value) || 0));
    const complete = values.every((v) => v !== '');
    const variant = complete ? data.variants.find((v) => v.options.every((o, i) => o === values[i])) : null;
    return { key: eye.dataset.bhLensEye, values, qty, complete, variant };
  };

  // Modus "gleiche Werte": nur noch eine Auswahl (rechts), Menge = Boxen für beide Augen, eine Warenkorb-Position
  const setBothMode = (on) => {
    root.classList.toggle('is-both', on);
    root.querySelectorAll('[data-bh-lens-legend], [data-bh-lens-qty-label]').forEach((el) => {
      if (el.closest('[data-bh-lens-eye="R"]')) el.textContent = on ? el.dataset.both : el.dataset.single;
    });
    const rQty = eyes[0].querySelector('[data-bh-lens-qty]');
    const lQty = eyes[1].querySelector('[data-bh-lens-qty]');
    if (on) {
      // Bisherige Menge beider Augen übernehmen (Standard: 1 + 1 = 2 Boxen)
      rQty.value = Math.max(1, (Number(rQty.value) || 0) + (Number(lQty.value) || 0));
    } else {
      const total = Number(rQty.value) || 0;
      rQty.value = Math.max(1, Math.ceil(total / 2));
      lQty.value = Math.max(1, Math.floor(total / 2));
    }
  };

  const update = () => {
    const both = same.checked;
    const states = both ? [read(eyes[0])] : eyes.map(read);
    if (!both) eyes.forEach((eye, i) => eye.classList.toggle('is-off', states[i].qty === 0));
    const active = states.filter((s) => s.qty > 0);
    const ready = active.length > 0 && active.every((s) => s.complete && s.variant && s.variant.available);
    let html = '';
    let total = 0;
    active.forEach((s) => {
      const label = both ? 'Beide Augen' : s.key === 'R' ? 'Rechts' : 'Links';
      if (s.variant) {
        const line = s.variant.price * s.qty;
        total += line;
        html += `<div class="bh-lens__row"><span>${label}: ${s.qty} × ${s.values.join(' / ')}</span><span>${formatMoney(line, data.money)}</span></div>`;
        if (!s.variant.available) html += `<p class="bh-lens__note">${label}: diese Stärke ist derzeit nicht lieferbar.</p>`;
      }
    });
    if (total > 0) {
      html += `<div class="bh-lens__row bh-lens__row--total"><span>Summe</span><span>${formatMoney(total, data.money)}</span></div>`;
      if (both) {
        const supply = supplyText(pack, days, active[0]?.qty || 0, 2);
        if (supply) html += `<p class="bh-lens__note">Reicht ${supply} für beide Augen</p>`;
      } else {
        const supply = active.map((s) => supplyText(pack, days, s.qty)).filter(Boolean);
        if (supply.length) {
          const uniq = [...new Set(supply)];
          html += `<p class="bh-lens__note">Reicht ${uniq.length === 1 ? uniq[0] : supply.join(' / ')}${active.length > 1 ? ' pro Auge' : ''}</p>`;
        }
      }
      if (free) {
        html += total >= free
          ? '<p class="bh-lens__note bh-lens__note--ok">✓ Gratis Versand innerhalb Österreichs</p>'
          : `<p class="bh-lens__note">Noch ${formatMoney(free - total, data.money)} bis zum Gratis-Versand in Österreich</p>`;
      }
    }
    summary.innerHTML = html;
    addBtn.disabled = !ready;
    errorEl.hidden = true;
  };

  root.addEventListener('change', (e) => {
    if (e.target.matches('select')) e.target.classList.remove('is-missing');
    doneEl.hidden = true;
    update();
  });
  root.addEventListener('input', (e) => { if (e.target.matches('[data-bh-lens-qty]')) update(); });
  root.addEventListener('click', (e) => {
    const minus = e.target.closest('[data-bh-lens-minus]');
    const plus = e.target.closest('[data-bh-lens-plus]');
    if (!minus && !plus) return;
    const input = e.target.closest('.bh-lens__qty').querySelector('input');
    input.value = Math.max(0, Math.min(10, (Number(input.value) || 0) + (plus ? 1 : -1)));
    doneEl.hidden = true;
    update();
  });

  addBtn.addEventListener('click', async () => {
    const both = same.checked;
    const states = (both ? [read(eyes[0])] : eyes.map(read)).filter((s) => s.qty > 0);
    const missing = states.some((s) => !s.complete);
    if (missing) {
      (both ? [eyes[0]] : eyes).forEach((eye) => eye.querySelectorAll('[data-bh-lens-opt]').forEach((sel) => sel.classList.toggle('is-missing', sel.value === '')));
      return;
    }
    const items = states.map((s) => ({ id: s.variant.id, quantity: s.qty, properties: { Auge: both ? 'Beide' : s.key === 'R' ? 'Rechts' : 'Links' } }));
    addBtn.disabled = true;
    try {
      const root_ = window.Shopify?.routes?.root || '/';
      const res = await fetch(`${root_}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Konnte nicht hinzugefügt werden.');
      }
      const cart = await fetch(`${root_}cart.js`).then((r) => r.json());
      document.dispatchEvent(new CartAddEvent(cart, 'bh-linsen', { source: 'bh-linsen', itemCount: cart.item_count }));
      doneEl.hidden = false;
      document.querySelector('cart-drawer-component')?.open?.();
    } catch (err) {
      errorEl.textContent = err.message;
      errorEl.hidden = false;
    } finally {
      addBtn.disabled = false;
    }
  });

  same.addEventListener('change', () => { setBothMode(same.checked); doneEl.hidden = true; update(); });
  update();
}

document.querySelectorAll('[data-bh-lens]').forEach(init);
